/**
 * Обороти рахунку (ч.1038): по місяцях / кварталах / роках з
 * кореспонденцією всередині періоду. Клік по періоду — картка за період,
 * по кореспондуючому рахунку — картка за період з відбором.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { accounts, reports, type PeriodRow } from '@/api/documents';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import LedgerFilters, { NoRegulatedAccess } from './LedgerFilters';
import { useLedgerParams } from './useLedgerParams';
import { balanceColumns, periodLabel, TreeLabel } from './ledgerColumns';
import { ledgerLinks, PERIODICITY, toggleCollapsed, visibleRows } from './ledgerModel';

type Row = PeriodRow & { _idx: string; _kind?: 'total' };

export default function AccountTurnoverPage() {
  const st = useLedgerParams();
  const navigate = useNavigate();
  const account = st.get('account');
  const per = st.get('per') || 'month';
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const accountsQ = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => accounts.list({ only_postable: false, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });
  const q = useQuery({
    queryKey: ['ledger-to', st.org, account, st.from, st.to, per],
    queryFn: () => reports.accountTurnover({ organization_id: st.org, account_code: account, date_from: st.from, date_to: st.to, periodicity: per }),
    enabled: !!st.org && !!account,
  });
  const data = q.data;
  const period = { org: st.org, from: st.from, to: st.to };
  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    const all: Row[] = data.rows.map((r, i) => ({ ...r, _idx: String(i) }));
    const out = visibleRows(all, collapsed, (r) => r._idx);
    out.push({ ...data.totals, kind: 'total', level: 0, label: 'Разом', account_code: data.account_code,
      period_start: data.date_from, period_end: data.date_to, corr_account_code: null, corr_account_name: null,
      is_group: false, _idx: '_total', _kind: 'total' });
    return out;
  }, [data, collapsed]);
  const cardLink = (r: Row) => ledgerLinks.accountCard({
    org: st.org, from: r.period_start, to: r.period_end, account, corr: r.corr_account_code });

  const cols = useMemo<ColumnDef<Row>[]>(() => [
    { key: 'label', header: 'Період / кор. рахунок', width: 300,
      cell: (r) => r._kind ? <strong>{r.label}</strong> : (
        <TreeLabel level={r.level} isGroup={r.is_group} collapsed={collapsed.has(r._idx)}
          onToggle={() => setCollapsed((s) => toggleCollapsed(s, r._idx))}>
          <a className="ledger__link" title={r.kind === 'period' ? 'Картка рахунку за цей період' : 'Картка за період з відбором по кор. рахунку'}
            onClick={(e) => { e.stopPropagation(); navigate(cardLink(r)); }}>{r.label}</a>
        </TreeLabel>) },
    ...balanceColumns<Row>(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [collapsed, account, st.org]);

  if (!st.permsLoading && !st.hasPermission) return <NoRegulatedAccess title="Обороти рахунку" total={st.totalAccessibleOrgs} />;
  const accList = (accountsQ.data ?? []).slice().sort((a, b) => a.code.localeCompare(b.code));
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{data ? `Обороти рахунку ${data.account_code} ${data.account_name}` : `Обороти рахунку ${account}`}
          {' '}<span className="ledger__subtitle">{periodLabel(st.from, st.to)}</span></h2>
        {account && (
          <span className="ledger__tree-actions">
            <Link className="btn-link" to={ledgerLinks.accountAnalysis({ ...period, account })}>аналіз рахунку</Link>
            <Link className="btn-link" to={ledgerLinks.trialBalanceAccount({ ...period, account })}>ОСВ по рахунку</Link>
          </span>
        )}
      </div>
      <LedgerFilters state={st}
        printHref={account ? ledgerLinks.print('to', { ...period, account, per }) : undefined}
        onExcel={account ? () => reports.ledgerXlsx('/reports/account-turnover', { organization_id: st.org, account_code: account, date_from: st.from, date_to: st.to, periodicity: per }) : undefined}
        disabled={!data}>
        <div className="field" style={{ minWidth: 320 }}>
          <label>Рахунок</label>
          <select value={account} onChange={(e) => st.set({ account: e.target.value })}>
            <option value="">— оберіть —</option>
            {accList.map((a) => <option key={a.id} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Період</label>
          <select value={per} onChange={(e) => st.set({ per: e.target.value === 'month' ? null : e.target.value })}>
            {PERIODICITY.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
      </LedgerFilters>
      {q.isError && <p className="ledger__warn">Не вдалося завантажити звіт: перевірте код рахунку.</p>}
      <DataTable<Row>
        rows={rows} columns={cols} rowKey={(r) => r._idx} loading={q.isLoading}
        emptyText={account ? 'За період рухів немає.' : 'Оберіть рахунок.'}
        rowBold={(r) => r.kind === 'period' || !!r._kind}
        rowBackground={(r) => r._kind ? 'var(--bg-soft)' : undefined}
        onRowDoubleClick={(r) => { if (!r._kind) navigate(cardLink(r)); }}
        storageKey="ledger-account-turnover"
      />
      {data && <div className="ledger__totals"><span className="ledger__hint">клік по періоду — картка за період; по кор. рахунку — картка з відбором</span></div>}
    </div>
  );
}
